import { useCallback, useRef, useState } from 'react';
import { ErrorToast } from '../../../adminComponents/Toasts/Toasts';
import { reportError } from '../../../helpers/reportError';
import { ConversationAttachmentType } from '../../../types/conversation.types';
import {
  attachmentTypeFor,
  MAX_ATTACHMENTS_PER_MESSAGE,
  uploadAttachment,
  validateAttachment,
} from '../chatUpload';

export type StagedAttachment = {
  /** Local key - the server id only exists once the upload finishes. */
  key: string;
  name: string;
  size: number;
  type: ConversationAttachmentType;
  /** Object URL for the thumbnail shown while staged. */
  previewUrl?: string;
  progress: number;
  attachmentId?: number;
  failed?: boolean;
};

let keyCounter = 0;
const nextKey = () => `staged-${(keyCounter += 1)}`;

/**
 * Stages files, uploads them straight to Spaces, and hands back the
 * `attachmentIds` to send with a message.
 *
 * Uploading is eager (on pick, not on send) so the send itself is instant and
 * the progress bars have somewhere to live.
 */
export const useChatAttachments = (conversationId: number | string | null) => {
  const [staged, setStaged] = useState<StagedAttachment[]>([]);
  const abortControllers = useRef(new Map<string, AbortController>());

  const patch = useCallback(
    (key: string, changes: Partial<StagedAttachment>) =>
      setStaged((current) =>
        current.map((item) =>
          item.key === key ? { ...item, ...changes } : item
        )
      ),
    []
  );

  const upload = useCallback(
    async (key: string, file: File, type: ConversationAttachmentType) => {
      if (conversationId === null) return;

      const controller = new AbortController();
      abortControllers.current.set(key, controller);

      try {
        const attachmentId = await uploadAttachment(conversationId, file, type, {
          signal: controller.signal,
          onProgress: (fraction) =>
            patch(key, { progress: Math.round(fraction * 100) }),
        });

        patch(key, { attachmentId, progress: 100 });
      } catch (error) {
        if (!controller.signal.aborted) {
          patch(key, { failed: true });
          reportError(error, {
            fallbackMessage: `Couldn't upload ${file.name}.`,
          });
        }
      } finally {
        abortControllers.current.delete(key);
      }
    },
    [conversationId, patch]
  );

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const incoming = Array.from(files);

      setStaged((current) => {
        const room = MAX_ATTACHMENTS_PER_MESSAGE - current.length;
        if (room <= 0) {
          ErrorToast(
            `You can attach up to ${MAX_ATTACHMENTS_PER_MESSAGE} files per message.`
          );
          return current;
        }

        const accepted: StagedAttachment[] = [];

        incoming.slice(0, room).forEach((file) => {
          const error = validateAttachment(file);
          if (error) {
            ErrorToast(error);
            return;
          }
          const type = attachmentTypeFor(file.type);
          if (!type) return;

          const key = nextKey();
          accepted.push({
            key,
            name: file.name,
            size: file.size,
            type,
            previewUrl:
              type === 'image' ? URL.createObjectURL(file) : undefined,
            progress: 0,
          });
          upload(key, file, type);
        });

        if (incoming.length > room) {
          ErrorToast(
            `Only ${room} more file${room === 1 ? '' : 's'} could be attached.`
          );
        }

        return [...current, ...accepted];
      });
    },
    [upload]
  );

  /** Stages an already-recorded voice note. */
  const addRecording = useCallback(
    (blob: Blob, mime: string) => {
      const extension = mime.includes('mp4') ? 'm4a' : 'ogg';
      const file = new File([blob], `voice-message.${extension}`, {
        type: mime,
      });
      addFiles([file]);
    },
    [addFiles]
  );

  const remove = useCallback((key: string) => {
    abortControllers.current.get(key)?.abort();
    setStaged((current) => {
      const target = current.find((item) => item.key === key);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return current.filter((item) => item.key !== key);
    });
  }, []);

  const clear = useCallback(() => {
    setStaged((current) => {
      current.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
      return [];
    });
  }, []);

  const attachmentIds = staged
    .map((item) => item.attachmentId)
    .filter((id): id is number => typeof id === 'number');

  return {
    staged,
    addFiles,
    addRecording,
    remove,
    clear,
    attachmentIds,
    hasStaged: staged.length > 0,
    /** True while any upload is still running - blocks send. */
    uploading: staged.some((item) => !item.attachmentId && !item.failed),
    hasFailed: staged.some((item) => item.failed),
  };
};
